import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { MenuCategoryTabs } from '../components/restaurant/MenuCategoryTabs';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, ArrowLeft } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuItem, CartItemOption } from '../lib/types';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useOpenOrderCheck } from '../lib/open-order-check';
import { OpenOrderAlert } from '../components/restaurant/OpenOrderAlert';
export function MenuPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restaurantLoading
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    addItem
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    openOrder,
    shouldAlert,
    dismiss: dismissOpenOrder
  } = useOpenOrderCheck(restaurant?.id);
  const [showOpenOrderAlert, setShowOpenOrderAlert] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    item: MenuItem;
    quantity: number;
    options: CartItemOption[];
    notes: string;
  } | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (restaurant?.id) {
      console.log('[NOIZA][MenuPage] Registering restaurant:', restaurant.id);
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    // No scroll — subcategories act as filters, content swaps in place
  };
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting add-to-cart — showing alert');
      setPendingAdd({
        item,
        quantity,
        options,
        notes
      });
      setShowOpenOrderAlert(true);
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name, restaurant.slug);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  const handleDismissOpenOrder = () => {
    dismissOpenOrder();
    setShowOpenOrderAlert(false);
    if (pendingAdd && restaurant) {
      const {
        item,
        quantity,
        options,
        notes
      } = pendingAdd;
      addItem({
        menu_item_id: item.id,
        restaurant_id: restaurant.id,
        item_name_snapshot: item.name,
        unit_price_snapshot: item.price,
        quantity,
        notes,
        image_url: item.image_url,
        selected_options: options,
        station_code: item.station_code || null
      }, restaurant.name, restaurant.slug);
      setPendingAdd(null);
    }
  };
  const handleCancelOpenOrder = () => {
    setShowOpenOrderAlert(false);
    setPendingAdd(null);
  };
  if (restaurantLoading || menuLoading) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5076">
        <MarketplaceHeader data-id="element-5077" />
        <div className="max-w-5xl mx-auto px-4 w-full mt-8" data-id="element-5078">
          <Skeleton className="h-12 w-1/3 mb-8" data-id="element-5079" />
          <div className="flex gap-4 mb-8" data-id="element-5080">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-24 rounded-full" data-id="element-5081" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5082">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32 rounded-2xl" data-id="element-5083" />)}
          </div>
        </div>
      </div>;
  }
  if (!restaurant) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5084">
        <MarketplaceHeader data-id="element-5085" />
        <div className="flex-1 flex items-center justify-center" data-id="element-5086">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-5087" />
        </div>
      </div>;
  }
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite pb-32" data-id="element-5088">
      <MarketplaceHeader data-id="element-5089" />

      {/* Mini Header */}
      <div className="bg-white border-b border-gray-100 py-4 sticky top-16 z-30" data-id="element-5090">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4" data-id="element-5091">
          <button onClick={() => navigate(`/r/${restaurant.slug}`)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-5092">
            <ArrowLeft className="w-5 h-5" data-id="element-5093" />
          </button>
          <div className="flex items-center gap-3" data-id="element-5094">
            {restaurant.logo_url && <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-md object-cover" data-id="element-5095" />}
            <h1 className="text-xl font-bold text-gray-900 leading-none" data-id="element-5096">
              {restaurant.name}
            </h1>
          </div>
        </div>
      </div>

      <MenuCategoryTabs categories={categories} activeCategory={activeCategory} onSelect={handleCategorySelect} primaryColor={primaryColor} data-id="element-5097" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-5098">
        {categories.length === 0 ? <EmptyState icon={Store} title="Menú no disponible" description="Este restaurante aún no ha configurado su menú." data-id="element-5099" /> : <AnimatePresence mode="wait" data-id="element-5100">
            {(() => {
          const activeCat = categories.find(c => c.id === activeCategory);
          const filteredItems = activeCat ? items.filter(item => item.category_id === activeCat.id) : [];
          if (!activeCat || filteredItems.length === 0) return null;
          return <motion.section key={activeCat.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} data-id="element-5101">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5102">
                    {activeCat.name}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5103">
                    {filteredItems.map(item => <MenuItemCard key={item.id} item={item} onClick={handleProductClick} primaryColor={primaryColor} data-id="element-5104" />)}
                  </div>
                </motion.section>;
        })()}
          </AnimatePresence>}
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-5105" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-5106" />

      {showOpenOrderAlert && openOrder && <OpenOrderAlert order={openOrder} onDismiss={handleDismissOpenOrder} onViewTracking={handleCancelOpenOrder} data-id="element-5107" />}
    </div>;
}